import React, { useEffect, useMemo, useRef, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Eye, Loader2, Send, Trash2, X } from "lucide-react";
import { Avatar } from "@/components/community/ChatList";
import { stormPulse } from "@/lib/pulse";

/** Barra de progreso: ya vista, en curso o pendiente. */
const progressStyle = (index, current) => {
  if (index < current) return { width: "100%" };
  if (index === current) return { animation: `storyFill ${DURATION}ms linear forwards` };
  return { width: 0 };
};

export const STORY_EMOJIS = ["❤️", "🔥", "😂", "😮", "😢", "👏"];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const mediaUrl = (m) => `${BACKEND}${m.url}`;

export const STORY_BGS = {
  rayo: "linear-gradient(145deg,#2A0206 0%,#7A0A14 55%,#FF3B3B 100%)",
  brasa: "linear-gradient(160deg,#160202 0%,#A31212 60%,#FF8A3B 100%)",
  noche: "linear-gradient(150deg,#05070C 0%,#1A1030 60%,#6D2BFF 100%)",
  sangre: "linear-gradient(135deg,#3B0008 0%,#3A1703 50%,#1A0003 100%)",
  humo: "linear-gradient(140deg,#0B0B0E 0%,#3A3A44 60%,#B9B9C6 100%)",
};

const DURATION = 6000;

/** Visor a pantalla completa de las historias de un autor. */
export const StoryViewer = ({ groups, startGroup, onClose, onChanged }) => {
  const [gi, setGi] = useState(startGroup);
  const [si, setSi] = useState(0);
  const [reply, setReply] = useState("");
  const [viewers, setViewers] = useState(null);
  const [live, setLive] = useState(null);
  const timer = useRef(null);

  const group = groups[gi];
  const current = group?.stories?.[si];
  const story = live?.story_id === current?.story_id ? live : current;
  const isVideo = story?.media?.mime?.startsWith("video/");

  const next = () => {
    if (!group) return;
    if (si + 1 < group.stories.length) return setSi(si + 1);
    if (gi + 1 < groups.length) { setGi(gi + 1); setSi(0); return; }
    onClose();
  };
  const prev = () => {
    if (si > 0) return setSi(si - 1);
    if (gi > 0) { setGi(gi - 1); setSi(0); return; }
  };

  useEffect(() => {
    setViewers(null); setReply(""); setLive(null);
    if (!story) return;
    userApi.post(`/stories/${story.story_id}/view`)
      .then(() => onChanged?.(true))
      .catch((e) => console.warn("No se pudo marcar la historia como vista:", e));
    if (isVideo) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(next, DURATION);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.story_id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gi, si]);

  if (!story) return null;

  const send = async () => {
    if (!reply.trim()) return;
    try {
      await userApi.post(`/stories/${story.story_id}/reply`, { text: reply.trim() });
      setReply("");
      toast.success("Respuesta enviada al chat");
    } catch (e) { toast.error(apiError(e, "No se pudo responder")); }
  };

  const react = async (emoji) => {
    try {
      const { data } = await userApi.post(`/stories/${story.story_id}/react`, { emoji });
      setLive(data);
      stormPulse();
    } catch (e) { toast.error(apiError(e, "No se pudo reaccionar")); }
  };

  const remove = async () => {
    try {
      await userApi.delete(`/stories/${story.story_id}`);
      toast.success("Historia eliminada");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(apiError(e, "No se pudo eliminar")); }
  };

  const showViewers = async () => {
    try {
      const { data } = await userApi.get(`/stories/${story.story_id}/viewers`);
      setViewers(data.viewers);
    } catch (e) { toast.error(apiError(e, "No se pudo cargar")); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-0 sm:p-6"
      style={{ background: "rgba(2,1,3,0.92)", backdropFilter: "blur(18px)" }}
      data-testid="story-viewer">
      <div className="absolute top-4 right-4 z-20">
        <button onClick={onClose} data-testid="story-close"
          className="glass-btn w-10 h-10 rounded-full flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-20 hidden sm:block">
        <button onClick={prev} data-testid="story-prev"
          className="glass-btn w-10 h-10 rounded-full flex items-center justify-center">
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20 hidden sm:block">
        <button onClick={next} data-testid="story-next"
          className="glass-btn w-10 h-10 rounded-full flex items-center justify-center">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="relative w-full h-full sm:w-[420px] sm:h-[740px] sm:max-h-[90vh] overflow-hidden sm:rounded-3xl border border-red-500/25"
        style={{ background: STORY_BGS[story.bg] || STORY_BGS.rayo }}>
        <div className="absolute top-0 left-0 right-0 p-3 z-10">
          <div className="flex gap-1.5" data-testid="story-progress">
            {group.stories.map((s, i) => (
              <div key={s.story_id} className="h-[3px] flex-1 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-white rounded-full" style={progressStyle(i, si)} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2.5 mt-3">
            <Avatar person={group.author} size={34} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{group.author.name}</div>
              <div className="text-[11px] text-white/60">
                {new Date(story.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center" onClick={next}>
          {story.media ? (
            isVideo ? (
              <video src={mediaUrl(story.media)} autoPlay controls onEnded={next}
                className="w-full h-full object-contain" data-testid="story-video" />
            ) : (
              <img src={mediaUrl(story.media)} alt="historia"
                className="w-full h-full object-contain" data-testid="story-image" />
            )
          ) : null}
          {story.text && (
            <div className={`absolute px-6 text-center ${story.media ? "bottom-24" : ""}`}>
              <p className="text-white font-heading font-bold text-2xl leading-snug drop-shadow-lg"
                data-testid="story-text">{story.text}</p>
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-3 z-10" onClick={(e) => e.stopPropagation()}>
          {story.mine ? (
            <div className="space-y-2">
              {viewers && (
                <div className="glass-card p-2 max-h-40 overflow-y-auto" data-testid="story-viewers">
                  {viewers.length === 0 && <div className="text-xs text-white/55 px-1 py-1.5">Nadie la ha visto todavía.</div>}
                  {viewers.map((v) => (
                    <div key={v.key} className="flex items-center gap-2 px-1 py-1.5">
                      <Avatar person={v} size={26} />
                      <span className="text-xs text-white/85 flex-1 truncate">{v.name}</span>
                      {v.reaction && <span className="text-sm">{v.reaction}</span>}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={showViewers} data-testid="story-views"
                  className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5" /> {story.views} visto(s)
                </button>
                {story.total_reactions > 0 && (
                  <div className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5"
                    data-testid="story-reaction-summary">
                    {Object.entries(story.reactions).map(([e, n]) => (
                      <span key={e}>{e} {n}</span>
                    ))}
                  </div>
                )}
                <button onClick={remove} data-testid="story-delete"
                  className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-1.5" data-testid="story-emojis">
                {STORY_EMOJIS.map((e, i) => (
                  <button key={e} onClick={() => react(e)} data-testid={`story-react-${i}`}
                    className={`story-emoji ${story.my_reaction === e ? "story-emoji-on" : ""}`}>
                    {e}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={`Responder a ${group.author.name.split(" ")[0]}…`}
                  data-testid="story-reply-input"
                  className="glass-field flex-1 px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/40 outline-none" />
                <button onClick={send} data-testid="story-reply-send"
                  className="glass-btn glass-btn-red w-11 h-11 rounded-xl flex items-center justify-center">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Compositor de historia: foto/video o texto con fondo. */
const StoryComposer = ({ onClose, onPosted }) => {
  const [text, setText] = useState("");
  const [media, setMedia] = useState(null);
  const [bg, setBg] = useState("rayo");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const attach = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await userApi.post("/community/media", fd);
      setMedia(data);
    } catch (e) { toast.error(apiError(e, "No se pudo subir el archivo")); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!text.trim() && !media) return toast.error("Escribe algo o sube una foto");
    setBusy(true);
    try {
      await userApi.post("/stories", { text: text.trim(), media_id: media?.media_id || null, bg });
      toast.success("Tu historia estará 24 horas en el muro");
      onPosted?.();
      onClose();
    } catch (e) { toast.error(apiError(e, "No se pudo publicar la historia")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      style={{ background: "rgba(2,1,3,0.9)", backdropFilter: "blur(18px)" }}
      data-testid="story-composer">
      <div className="glass-card p-5 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-heading font-bold text-white text-base">Nueva historia · 24 h</div>
          <button onClick={onClose} data-testid="story-composer-close"
            className="glass-btn w-9 h-9 rounded-full flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="rounded-2xl h-56 flex items-center justify-center overflow-hidden border border-red-500/20"
          style={{ background: STORY_BGS[bg] }}>
          {media && media.mime?.startsWith("video/") && (
            <video src={mediaUrl(media)} className="w-full h-full object-cover" controls />
          )}
          {media && !media.mime?.startsWith("video/") && (
            <img src={mediaUrl(media)} alt="previa" className="w-full h-full object-cover" />
          )}
          {!media && (
            <p className="px-5 text-center text-white font-heading font-bold text-xl">
              {text || "Tu historia"}
            </p>
          )}
        </div>

        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={400}
          placeholder="Escribe tu historia" data-testid="story-text-input"
          className="glass-field w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/40 outline-none" />

        <div className="flex items-center gap-2">
          {Object.keys(STORY_BGS).map((k) => (
            <button key={k} onClick={() => setBg(k)} data-testid={`story-bg-${k}`}
              className={`w-8 h-8 rounded-full border-2 ${bg === k ? "border-red-400" : "border-white/15"}`}
              style={{ background: STORY_BGS[k] }} />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="story-attach"
            className="glass-btn px-3 py-2 rounded-xl text-xs">
            {media ? "Cambiar foto o video" : "Foto o video"}
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden"
            data-testid="story-file" onChange={(e) => attach(e.target.files?.[0])} />
          <button onClick={publish} disabled={busy} data-testid="story-publish"
            className="glass-btn glass-btn-red px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publicar
          </button>
        </div>
      </div>
    </div>
  );
};

/** Fila de Historias Del Día arriba del muro. */
export const Stories = ({ me, refreshKey }) => {
  const [groups, setGroups] = useState([]);
  const others = useMemo(
    () => groups.map((g, index) => ({ ...g, index })).filter((g) => !g.mine), [groups]);
  const [open, setOpen] = useState(null);
  const [composing, setComposing] = useState(false);

  const load = async () => {
    try {
      const { data } = await userApi.get("/stories");
      setGroups(data.groups || []);
    } catch (e) { console.warn("No se pudieron cargar las historias:", e); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const mine = groups.find((g) => g.mine);

  return (
    <>
      <div className="glass-card p-3" data-testid="stories-bar">
        <div className="flex gap-3 overflow-x-auto no-scrollbar">
          <button onClick={() => (mine ? setOpen(groups.indexOf(mine)) : setComposing(true))}
            data-testid="story-add" className="shrink-0 flex flex-col items-center gap-1.5 w-[72px]">
            <div className="relative">
              <div className={`rounded-full p-[2px] ${mine ? "story-ring-live" : "bg-white/15"}`}>
                <div className="rounded-full p-[2px]" style={{ background: "rgba(8,3,5,0.9)" }}>
                  <Avatar person={me} size={54} />
                </div>
              </div>
              <span onClick={(e) => { e.stopPropagation(); setComposing(true); }}
                data-testid="story-add-plus"
                className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full flex items-center justify-center text-white text-base font-bold border-2 border-black/70"
                style={{ background: "linear-gradient(135deg,#B4560E,#2A1403)" }}>+</span>
            </div>
            <span className="text-[11px] text-white/75 truncate w-full text-center">
              {mine ? "Tu historia" : "Añadir"}
            </span>
          </button>

          {others.map((g) => (
            <button key={g.author.key} onClick={() => setOpen(g.index)}
              data-testid={`story-open-${g.author.key}`}
              className="shrink-0 flex flex-col items-center gap-1.5 w-[72px]">
              <div className={`rounded-full p-[2px] ${g.unseen > 0 ? "story-ring-live" : "bg-white/15"}`}>
                <div className="rounded-full p-[2px]" style={{ background: "rgba(8,3,5,0.9)" }}>
                  <Avatar person={g.author} size={54} />
                </div>
              </div>
              <span className="text-[11px] text-white/75 truncate w-full text-center">{g.author.name}</span>
            </button>
          ))}

          {groups.length === 0 && (
            <div className="flex items-center text-xs text-white/45 px-2" data-testid="stories-empty">
              Comparte la primera historia del día — desaparece en 24 horas.
            </div>
          )}
        </div>
      </div>

      {composing && <StoryComposer onClose={() => setComposing(false)} onPosted={load} />}
      {open !== null && groups[open] && (
        <StoryViewer groups={groups} startGroup={open} onClose={() => setOpen(null)}
          onChanged={(soft) => { if (!soft) load(); }} />
      )}
    </>
  );
};

export default Stories;
